import { getCollection } from './firestoreService';
import { aggregateSubscriptions } from './subscriptionAggregator';
import { getAnalyticsEvents, getUserEngagementInsights } from './analyticsService';
import { getRevenueAnalytics } from './revenueService';
import { getRetentionAnalytics } from './retentionService';
import { getTrendsAnalytics } from './trendsService';
import { getOnboardingAnalytics } from './onboardingService';
import { getAllSubscriptions } from './subscriptionService';
import { getActiveSessions } from './activeSessionsService';
import { getCrashAnalytics } from './crashAnalyticsService';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../config/firebase';
import { subDays } from 'date-fns';

const functions = getFunctions(app);

/**
 * Dashboard context sent to the LLM for AI-generated insights.
 * Aggregate metrics only; no PII.
 */
export interface DashboardContext {
  overview?: { totalUsers: number; activeSubscriptions: number; mrr: number; totalEvents: number };
  users?: { total: number; newLast30Days: number };
  revenue?: { mrr: number; arpu: number; subscriptionRate: number };
  engagement?: {
    totalSearches: number;
    totalTopicsAdded: number;
    searchToAddRate?: number;
    topTopic?: { title: string; count: number };
    topSearchTerm?: { word: string; count: number };
  };
  retention?: {
    returnRate?: number;
    latestCohortD1?: number;
    latestCohortD7?: number;
  };
  trends?: { momGrowth: number; yoyGrowth: number; compoundGrowth: number };
  onboarding?: {
    funnelSteps?: Array<{ step: string; count: number; conversionRate: number }>;
    onboardingCompletionRate?: number;
  };
  platform?: { iosSubs: number; androidSubs: number; stripeSubs: number };
  supportIssues?: { count: number; topTypes?: string[] };
  activeSessions?: number;
  crashCountLast24h?: number;
}

/**
 * Builds a dashboard context object from all available analytics services
 * for use with the adminAnalyticsInsights Cloud Function.
 */
export const buildDashboardContext = async (
  options: { days?: number; retentionDays?: number } = {}
): Promise<DashboardContext> => {
  const days = options.days ?? 30;
  const retentionDays = options.retentionDays ?? 90;

  const [
    users,
    metrics,
    engagementInsights,
    revenueData,
    retentionData,
    trendsData,
    onboardingData,
    subscriptions,
  ] = await Promise.all([
    getCollection('users', undefined, undefined, undefined, 10000).catch(() => []),
    aggregateSubscriptions().catch(() => ({ activeSubscriptions: 0, totalMRR: 0 })),
    getUserEngagementInsights().catch(() => null),
    getRevenueAnalytics(days).catch(() => null),
    getRetentionAnalytics(retentionDays, 'monthly').catch(() => null),
    getTrendsAnalytics(Math.min(retentionDays, 90)).catch(() => null),
    getOnboardingAnalytics(days).catch(() => null),
    getAllSubscriptions().catch(() => []),
  ]);

  const totalUsers = users.length;
  const activeSubscriptions = metrics?.activeSubscriptions ?? 0;
  const mrr = metrics?.totalMRR ?? revenueData?.totalRevenue ?? 0;
  const subscriptionRate = totalUsers > 0 ? (activeSubscriptions / totalUsers) * 100 : 0;
  const arpu = revenueData?.averageRevenuePerUser ?? 0;

  let totalEvents = 0;
  try {
    const events = await getAnalyticsEvents(days);
    totalEvents = events.length;
  } catch {
    totalEvents = 0;
  }

  const now = new Date();
  const cutoff = subDays(now, 30);
  const newLast30Days = users.filter((u: any) => {
    const created = u.createdAt?.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
    return created >= cutoff;
  }).length;

  const context: DashboardContext = {
    overview: {
      totalUsers,
      activeSubscriptions,
      mrr,
      totalEvents,
    },
    users: { total: totalUsers, newLast30Days },
    revenue: { mrr, arpu, subscriptionRate },
  };

  if (engagementInsights) {
    const totalSearches = engagementInsights.totalSearches || 0;
    const totalTopicsAdded = engagementInsights.totalTopicsAdded || 0;
    context.engagement = {
      totalSearches,
      totalTopicsAdded,
      searchToAddRate: totalSearches > 0 ? (totalTopicsAdded / totalSearches) * 100 : undefined,
      topTopic: engagementInsights.popularTopics?.[0]
        ? { title: engagementInsights.popularTopics[0].title, count: engagementInsights.popularTopics[0].count }
        : undefined,
      topSearchTerm: engagementInsights.searchWordCounts?.[0]
        ? { word: engagementInsights.searchWordCounts[0].word, count: engagementInsights.searchWordCounts[0].count }
        : undefined,
    };
  }

  if (retentionData) {
    const latestCohort = retentionData.cohorts?.[retentionData.cohorts.length - 1];
    context.retention = {
      returnRate: retentionData.retentionMetrics?.returnRate,
      latestCohortD1: latestCohort?.retentionRates?.d1,
      latestCohortD7: latestCohort?.retentionRates?.d7,
    };
  }

  if (trendsData?.growthRates) {
    context.trends = {
      momGrowth: trendsData.growthRates.mom,
      yoyGrowth: trendsData.growthRates.yoy,
      compoundGrowth: trendsData.growthRates.compound,
    };
  }

  if (onboardingData) {
    context.onboarding = {
      funnelSteps: onboardingData.funnel?.map((s) => ({
        step: s.step,
        count: s.count,
        conversionRate: s.conversionRate,
      })),
      onboardingCompletionRate: onboardingData.activationMetrics?.onboardingCompletionRate,
    };
  }

  context.platform = {
    iosSubs: subscriptions.filter((s: any) => s.platform === 'ios').length,
    androidSubs: subscriptions.filter((s: any) => s.platform === 'android').length,
    stripeSubs: subscriptions.filter((s: any) => s.platform === 'stripe').length,
  };

  try {
    const sessions = await getActiveSessions();
    context.activeSessions = sessions.totalActiveSessions;
  } catch {
    context.activeSessions = undefined;
  }

  try {
    const crash = await getCrashAnalytics(7);
    context.crashCountLast24h = crash.totalLast24h;
  } catch {
    context.crashCountLast24h = undefined;
  }

  return context;
};

export interface GenerateAIInsightsResult {
  success: true;
  insights: string;
}

export interface GenerateAIInsightsError {
  success: false;
  error: string;
}

/**
 * Calls the adminAnalyticsInsights Cloud Function with the given context.
 * Requires admin auth and OPENAI_API_KEY to be set in Firebase Secret Manager.
 */
export const generateAIInsights = async (
  context: DashboardContext
): Promise<GenerateAIInsightsResult | GenerateAIInsightsError> => {
  try {
    const callable = httpsCallable<{ dashboardContext: DashboardContext }, { insights: string }>(
      functions,
      'adminAnalyticsInsights'
    );
    const result = await callable({ dashboardContext: context });
    const insights = result.data?.insights?.trim();
    if (!insights) {
      return { success: false, error: 'No insights returned from AI.' };
    }
    return { success: true, insights };
  } catch (err: any) {
    const message =
      err?.message ||
      err?.code ||
      (typeof err === 'string' ? err : 'Failed to generate AI insights.');
    return { success: false, error: message };
  }
};
